import { useUser, useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BACKEND_URL } from "@/constants/api";
import { isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import {
  ArrowRight,
  CheckCircle2,
  X,
  Plus,
  FolderPlus,
  Files,
  FileDown,
  MoveRight,
  MoveDown,
} from "lucide-react";
import { ReportFolderCard } from "@/components/entities/ReportFolderCard";

function StepConnector() {
  return (
    <div className="flex justify-center items-center py-2 md:py-0 md:px-2 text-muted-foreground">
      <MoveDown size={26} strokeWidth={1.5} className="md:hidden" />
      <MoveRight size={30} strokeWidth={1.5} className="hidden md:block" />
    </div>
  );
}

function OnboardingStep({ number, title, description, illustration }) {
  return (
    <div className="flex h-full flex-col items-center text-center rounded-xl border border-border bg-card shadow-sm px-5 py-7 md:px-6 md:py-8">
      <div className="mb-5 h-16 w-16 flex items-center justify-center">
        {illustration}
      </div>
      <span className="text-[0.6875rem] font-semibold tracking-[0.22em] text-primary mb-2">
        {number}
      </span>
      <h4 className="text-[0.9375rem] font-semibold text-foreground mb-1.5 leading-snug">
        {title}
      </h4>
      <p className="text-[0.8125rem] text-muted-foreground leading-relaxed">
        {description}
      </p>
    </div>
  );
}

function DashboardOnboarding() {
  return (
    <div className="rounded-2xl bg-muted/40 overflow-hidden">
      <div className="px-5 py-10 md:px-10 md:py-12">
        <div className="mb-9 text-center">
          <p className="text-[0.6875rem] font-semibold tracking-[0.22em] uppercase text-muted-foreground/80 mb-3">
            Getting Started
          </p>
          <h3 className="font-display text-2xl md:text-[1.75rem] font-normal text-foreground tracking-tight leading-tight">
            Three steps to your first report card
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-y-2 md:gap-x-3 mb-10">
          <OnboardingStep
            number="01"
            title="Create a Report Folder"
            description="One folder per class, per reporting cycle."
            illustration={
              <FolderPlus
                size={52}
                strokeWidth={1.5}
                className="text-foreground/75"
              />
            }
          />
          <StepConnector />
          <OnboardingStep
            number="02"
            title="Add report cards"
            description="One inside the folder for each student."
            illustration={
              <Files
                size={52}
                strokeWidth={1.5}
                className="text-foreground/75"
              />
            }
          />
          <StepConnector />
          <OnboardingStep
            number="03"
            title="Generate &amp; download"
            description="Export filled PDFs with one click."
            illustration={
              <FileDown
                size={52}
                strokeWidth={1.5}
                className="text-foreground/75"
              />
            }
          />
        </div>

        <div className="flex justify-center">
          <Link to="/report-folders">
            <Button size="lg" className="gap-2">
              <Plus size={15} />
              Create your first Report Folder
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}


export default function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [initError, setInitError] = useState(false);
  const [recentReportFolders, setRecentReportFolders] = useState([]);
  const [reportFoldersLoading, setReportFoldersLoading] = useState(true);
  const previewOnboarding =
    import.meta.env.DEV && searchParams.get("preview") === "onboarding";
  useEffect(() => {
    const ac = new AbortController();
    const init = async () => {
      try {
        const token = await getToken();
        const folderRes = await fetch(`${BACKEND_URL}api/report-folders/recent?limit=6`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (folderRes.ok) {
          const recent = await folderRes.json();
          setRecentReportFolders(recent);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        setInitError(true);
      } finally {
        if (!ac.signal.aborted) setReportFoldersLoading(false);
      }
    };

    if (user) init();

    const query = new URLSearchParams(window.location.search);
    if (query.get("success")) {
      setPaymentSuccess(true);
      navigate("/dashboard", { replace: true });
    }
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const firstName =
    user?.firstName || user?.fullName?.split(" ")[0] || "Teacher";

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      {paymentSuccess && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-success-border bg-success-bg px-4 py-3 text-success-fg text-sm mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="shrink-0" />
            <span>Payment successful! Your plan has been upgraded.</span>
          </div>
          <button
            onClick={() => setPaymentSuccess(false)}
            className="shrink-0 opacity-70 hover:opacity-100"
          >
            <X size={15} />
          </button>
        </div>
      )}
      {initError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive-border bg-destructive-bg px-4 py-3 text-destructive-fg text-sm mb-4">
          <div className="flex items-center gap-2">
            <X size={16} className="shrink-0" />
            <span>Could not connect to the server. Please refresh the page.</span>
          </div>
          <button
            onClick={() => setInitError(false)}
            className="shrink-0 opacity-70 hover:opacity-100"
          >
            <X size={15} />
          </button>
        </div>
      )}

      <PageHeader
        title={`${getGreeting()}, ${firstName}.`}
        subtitle={
          !reportFoldersLoading &&
          recentReportFolders.length > 0 &&
          !previewOnboarding
            ? "Pick up where you left off."
            : undefined
        }
      />

      {reportFoldersLoading && !previewOnboarding ? (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-0">Recent Report Folders</h2>
            <Link to="/report-folders" className="inline-flex items-center gap-1 text-[0.8125rem] font-medium text-primary no-underline hover:opacity-75 transition-opacity">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="flex flex-wrap gap-[0.625rem]">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-[76px] w-[380px] rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        </div>
      ) : recentReportFolders.length === 0 || previewOnboarding ? (
        <DashboardOnboarding />
      ) : (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-0">Recent Report Folders</h2>
            <Link to="/report-folders" className="inline-flex items-center gap-1 text-[0.8125rem] font-medium text-primary no-underline hover:opacity-75 transition-opacity">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="flex flex-wrap gap-[0.625rem]">
            {recentReportFolders.map((folder) => (
              <div key={folder._id} className="relative">
                <ReportFolderCard
                  item={folder}
                  getLinkPath={(item) =>
                    item.isShared
                      ? `/shared-report-folders/${item._id}`
                      : `/report-folders/${item._id}`
                  }
                />
                <span className="absolute top-2 right-2 z-20 pointer-events-none text-[10px] font-medium text-neutral-400 dark:text-white/40 bg-white/80 dark:bg-black/30 border border-neutral-200 dark:border-white/10 px-1.5 py-0.5 rounded-md backdrop-blur-sm">
                  {timeAgo(folder.updatedAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {import.meta.env.DEV && (
        <Link
          to={previewOnboarding ? "/dashboard" : "/dashboard?preview=onboarding"}
          className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-1.5 rounded-full border border-border bg-foreground/90 text-background px-3 py-1.5 text-[0.6875rem] font-mono tracking-tight no-underline shadow-lg backdrop-blur-sm hover:bg-foreground transition-colors"
          title="Dev only — toggles dashboard onboarding preview"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-background/80" />
          {previewOnboarding ? "Exit preview" : "Preview onboarding"}
        </Link>
      )}
    </div>
  );
}
